import CloseCircleXIcon from '@assets/icons/close-circle-x-large.svg';
import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  handleWebKeyPress,
  isShiftPressed,
  Key,
} from '@platform/shortcuts/utils/keyboard-platform';
import {
  NativeSyntheticEvent,
  Platform,
  TextInput,
  TextInputKeyPressEventData,
  TouchableOpacity,
  View,
} from 'react-native';
import { SearchInput as SearchInputAtom } from '@usepepper/ui/atoms/search-input';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useEscapeKeyClose } from '@platform/shortcuts/hooks/use-escape-key-close';

const SEARCH_RESET_BUTTON_SIZE = 24;

export interface CatalogSearchInputLabels {
  catalogModePlaceholder: string;
  catalogSearchCta: string;
  clearSearch: string;
  defaultPlaceholder: string;
  keyboardHint: string;
}

export interface CatalogSearchInputProps {
  isInCatalogMode?: boolean;
  isLoading?: boolean;
  labels: CatalogSearchInputLabels;
  maxLength?: number;
  minCatalogSearchLength?: number;
  onBlur?: () => void;
  onCatalogSearch?: (text: string) => void;
  onClearAndExit?: () => void;
  onEscapePress?: () => void;
  onFocus?: () => void;
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void;
  onSearchChange: (text: string) => void;
  onTabForward?: () => void;
}

export interface CatalogSearchInputRef {
  blur: () => void;
  clear: () => void;
  focus: () => void;
}

export const CatalogSearchInput = memo(
  forwardRef<CatalogSearchInputRef, CatalogSearchInputProps>(
    (
      {
        isInCatalogMode,
        isLoading,
        labels,
        maxLength,
        minCatalogSearchLength = 1,
        onBlur,
        onCatalogSearch,
        onClearAndExit,
        onEscapePress,
        onFocus,
        onKeyPress,
        onSearchChange,
        onTabForward,
      },
      ref,
    ) => {
      const [styles, theme] = useStyles();
      const inputRef = useRef<TextInput>(null);
      const [searchText, setSearchText] = useState('');

      const handleSearchChange = useCallback(
        (text: string) => {
          setSearchText(text);
          onSearchChange(text);
        },
        [onSearchChange],
      );

      useImperativeHandle(ref, () => ({
        blur: () => inputRef.current?.blur(),
        clear: () => setSearchText(''),
        focus: () => inputRef.current?.focus(),
      }));

      // Escape key handler via dedicated key event listener
      useEscapeKeyClose(onEscapePress);

      // Default mode handlers
      const handleDefaultClearSearch = () => {
        setSearchText('');
        onSearchChange('');
      };

      // Catalog mode handlers
      const handleCatalogClearSearch = () => {
        onClearAndExit?.();
      };

      const handleClearSearch = isInCatalogMode
        ? handleCatalogClearSearch
        : handleDefaultClearSearch;

      // Default mode: Enter triggers catalog search
      // Also call parent's onKeyPress (from useInputKeyOverride) to handle ArrowDown
      const handleDefaultKeyPress = (
        e: NativeSyntheticEvent<TextInputKeyPressEventData>,
      ) => {
        // Call parent's onKeyPress first (handles ArrowDown override)
        onKeyPress?.(e);

        // If ArrowDown was handled, onKeyPress will have prevented default
        if (e.nativeEvent.key === Key.ArrowDown) {
          return;
        }

        // Tab without Shift: focus first quantity. Shift+Tab: allow default (focus previous).
        if (e.nativeEvent.key === Key.Tab) {
          if (isShiftPressed(e)) {
            return;
          }
          if (onTabForward) {
            e.preventDefault?.();
            onTabForward();
          }
          return;
        }

        if (e.nativeEvent.key === Key.Escape) {
          e.preventDefault();
          onEscapePress?.();
          return;
        }

        if (e.nativeEvent.key === Key.Enter) {
          e.preventDefault();
          onCatalogSearch?.(searchText);
          return;
        }
        handleWebKeyPress(e);
      };

      // Catalog mode: call parent's onKeyPress (from useInputKeyOverride) to handle ArrowDown
      const handleCatalogKeyPress = (
        e: NativeSyntheticEvent<TextInputKeyPressEventData>,
      ) => {
        // Call parent's onKeyPress first (handles ArrowDown override)
        onKeyPress?.(e);

        // If ArrowDown was handled, onKeyPress will have prevented default
        if (e.nativeEvent.key === Key.ArrowDown) {
          return;
        }

        // Allows consumer to handle Tab key press, currently used to focus first quantity
        if (e.nativeEvent.key === Key.Tab) {
          if (isShiftPressed(e)) {
            return;
          }
          if (onTabForward) {
            e.preventDefault?.();
            onTabForward();
          }
          return;
        }

        if (e.nativeEvent.key === Key.Escape) {
          e.preventDefault();
          onEscapePress?.();
          return;
        }

        if (e.nativeEvent.key === Key.Enter) {
          e.preventDefault();
          return;
        }

        handleWebKeyPress(e);
      };

      const handleKeyPress = isInCatalogMode
        ? handleCatalogKeyPress
        : handleDefaultKeyPress;

      // Native only: Enter triggers onSubmitEditing instead of onKeyPress.
      // On web, Enter is handled via onKeyPress with preventDefault.
      const handleSubmitEditing = useCallback(() => {
        if (!isInCatalogMode) {
          onCatalogSearch?.(searchText);
        }
        inputRef.current?.blur();
      }, [isInCatalogMode, onCatalogSearch, searchText]);

      const showCatalogSearchCta =
        searchText.trim().length >= minCatalogSearchLength &&
        !isInCatalogMode &&
        !isLoading;

      const isClearHidden = searchText.length === 0;

      const placeholderText = isInCatalogMode
        ? labels.catalogModePlaceholder
        : labels.defaultPlaceholder;

      return (
        <View style={styles.container}>
          <SearchInputAtom
            maxLength={maxLength}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyPress={handleKeyPress}
            onSearch={handleSearchChange}
            onSubmitEditing={
              Platform.OS !== 'web' ? handleSubmitEditing : undefined
            }
            placeholderText={placeholderText}
            ref={inputRef}
            rightElement={
              <View style={styles.rightContainer}>
                {showCatalogSearchCta ? (
                  <TouchableOpacity
                    accessibilityLabel={labels.catalogSearchCta}
                    accessibilityRole="button"
                    onPress={() => onCatalogSearch?.(searchText)}
                    style={styles.catalogSearchCta}
                  >
                    <Typography variant="paragraph-sm-600">
                      {labels.catalogSearchCta}
                    </Typography>
                    {Platform.OS === 'web' ? (
                      <View style={styles.keyboardHint}>
                        <Typography
                          color={theme.sk.color.text.secondary}
                          variant="paragraph-sm-600"
                        >
                          {labels.keyboardHint}
                        </Typography>
                      </View>
                    ) : null}
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  accessibilityElementsHidden={isClearHidden}
                  accessibilityLabel={labels.clearSearch}
                  accessibilityRole="button"
                  aria-hidden={isClearHidden}
                  disabled={isClearHidden}
                  hitSlop={20}
                  importantForAccessibility={
                    isClearHidden ? 'no-hide-descendants' : 'auto'
                  }
                  onPress={handleClearSearch}
                  style={[
                    styles.clearButton,
                    isClearHidden && styles.clearButtonNotClickable,
                  ]}
                >
                  <CloseCircleXIcon
                    height={SEARCH_RESET_BUTTON_SIZE}
                    width={SEARCH_RESET_BUTTON_SIZE}
                  />
                </TouchableOpacity>
              </View>
            }
            searchText={searchText}
            selectOnFocus
            submitBehavior={Platform.OS !== 'web' ? 'submit' : undefined}
          />
        </View>
      );
    },
  ),
);

const useStyles = createStyles((theme) => ({
  catalogSearchCta: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingLeft: theme.sk.spacing.spaceSmall,
    paddingRight: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
    shadowColor: '#000',
    shadowOffset: { height: 2, width: 0 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
  },
  clearButton: {
    borderRadius: 32,
  },
  clearButtonNotClickable: {
    opacity: 0,
    pointerEvents: 'none',
  },
  container: {
    flex: 1,
  },
  keyboardHint: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusSmall,
    borderWidth: 1,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  rightContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
